import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { IconButton } from './button';
import { Moon, Sun } from './icons';

/* ────────────────────────────────────────────────────────────────────────────
   Theme. Dark is the product's native mode; light is a first-class alternative.
   The choice lives in `localStorage['bull.theme']` and is mirrored onto
   `document.documentElement.dataset.theme`, which is what the palette keys off.
   ──────────────────────────────────────────────────────────────────────────── */

export type Theme = 'dark' | 'light';

const STORAGE_KEY = 'bull.theme';

/** Private-mode Safari throws on `localStorage`; never let that break the app. */
function readStored(): Theme | null {
  try {
    const value = window.localStorage.getItem(STORAGE_KEY);
    return value === 'dark' || value === 'light' ? value : null;
  } catch {
    return null;
  }
}

function writeStored(theme: Theme) {
  try {
    window.localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    /* storage blocked — the in-memory theme still applies for this session */
  }
}

export interface ThemeApi {
  theme: Theme;
  setTheme: (t: Theme) => void;
  toggle: () => void;
}

const ThemeContext = createContext<ThemeApi | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() => {
    if (typeof window === 'undefined') return 'dark';
    return readStored() ?? 'dark';
  });

  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.documentElement.dataset.theme = theme;
  }, [theme]);

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next);
    writeStored(next);
  }, []);

  const toggle = useCallback(() => {
    setThemeState((current) => {
      const next: Theme = current === 'dark' ? 'light' : 'dark';
      writeStored(next);
      return next;
    });
  }, []);

  const value = useMemo<ThemeApi>(() => ({ theme, setTheme, toggle }), [theme, setTheme, toggle]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeApi {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used inside <ThemeProvider>');
  return ctx;
}

export interface ThemeToggleProps {
  className?: string;
}

/** Sun/Moon swap. Both glyphs are mounted so the crossfade has no pop-in. */
export function ThemeToggle({ className }: ThemeToggleProps) {
  const { theme, toggle } = useTheme();
  const dark = theme === 'dark';

  return (
    <IconButton
      label={dark ? 'Switch to light theme' : 'Switch to dark theme'}
      variant="ghost"
      onClick={toggle}
      aria-pressed={!dark}
      className={cn('relative overflow-hidden', className)}
    >
      <Sun
        size={16}
        className={cn(
          'absolute transition-all duration-300 ease-swift',
          dark ? 'rotate-90 scale-50 opacity-0' : 'rotate-0 scale-100 opacity-100',
        )}
      />
      <Moon
        size={16}
        className={cn(
          'absolute transition-all duration-300 ease-swift',
          dark ? 'rotate-0 scale-100 opacity-100' : '-rotate-90 scale-50 opacity-0',
        )}
      />
    </IconButton>
  );
}
